import type { ColumnCatalogDef } from '@/types/column-catalog'
import { createColumnDef as col } from '@/components/grid/column-builder'
import {
  COUNTRY_OPTIONS,
  EXCHANGE_OPTIONS,
  INDUSTRY_OPTIONS,
  SECTOR_OPTIONS,
  SHARE_GROUP_OPTIONS,
} from '@/column-registry/enum-options'

// ─── Price & Performance ───

const PRICE_PERFORMANCE: ColumnCatalogDef[] = [
  col('session_lp', 'Price', 'currency', 'Price & Performance', { formLabel: 'Last Price' }),
  col('session_chg', 'Chg $', 'currency', 'Price & Performance', { formLabel: 'Change $' }),
  col('session_chg_p', 'Chg %', 'percent', 'Price & Performance', {
    formLabel: 'Change %',
  }),
  col('pm_chg', 'PM Gap', 'currency', 'Price & Performance', { formLabel: 'PM Gap' }),
  col('pm_chg_p', 'PM Gap %', 'percent', 'Price & Performance', { formLabel: 'PM Gap %' }),
  col('o', 'Open', 'currency', 'Price & Performance'),
  col('h', 'High', 'currency', 'Price & Performance'),
  col('l', 'Low', 'currency', 'Price & Performance'),
]

// ─── Volume ───

const VOLUME: ColumnCatalogDef[] = [
  col('session_v', 'Vol', 'volume', 'Volume', { formLabel: 'Volume' }),
  col('rvol', 'RVOL', 'number', 'Volume'),
  // Dollar volume is a big-dollar magnitude, NOT share volume — pin to
  // `largePrice` so it doesn't pair with session_v / pm_v.
  col('dollar_vol', 'Dollar Vol', 'volume', 'Volume', { columnType: 'largePrice' }),
  col('pm_v', 'PM Vol', 'volume', 'Volume'),
]

// ─── Share Structure ───

const SHARE_STRUCTURE: ColumnCatalogDef[] = [
  col('marketcap', 'Market Cap', 'volume', 'Share Structure'),
  col('sharesoutstanding', 'Shares Out', 'volume', 'Share Structure'),
  col('sharesfloat', 'Float', 'volume', 'Share Structure'),
  col('percentinsiders', 'Insider Own %', 'percent', 'Share Structure'),
  col('percentinstitutions', 'Inst. Own %', 'percent', 'Share Structure'),
  col('shortpercentfloat', 'Short Float', 'percent', 'Share Structure'),
  col('floatrotation', 'Float Rotation', 'number', 'Share Structure'),
]

// ─── Financials ───

const FINANCIALS: ColumnCatalogDef[] = [
  col('eps', 'EPS', 'currency', 'Financials'),
  col('dilutedeps', 'Diluted EPS', 'currency', 'Financials'),
  // Income-statement dollar amounts → largePrice (consistent with scanner).
  col('revenue', 'Revenue', 'volume', 'Financials', { columnType: 'largePrice' }),
  col('netincome', 'Net Income', 'volume', 'Financials', { columnType: 'largePrice' }),
  col('peratio', 'P/E Ratio', 'number', 'Financials'),
  col('eps_growth_q', 'EPS QoQ Growth %', 'percent', 'Financials'),
  col('revenue_growth_q', 'Revenue QoQ %', 'percent', 'Financials'),
]

// ─── Company ───

const COMPANY: ColumnCatalogDef[] = [
  col('ticker', 'Ticker', 'string', 'Company'),
  col('name', 'Company Name', 'string', 'Company'),
  col('country', 'Country', 'enum', 'Company', { enumOptions: COUNTRY_OPTIONS }),
  col('share_group', 'Share Group', 'enum', 'Company', { enumOptions: SHARE_GROUP_OPTIONS }),
  col('exchange', 'Exchange', 'enum', 'Company', { enumOptions: EXCHANGE_OPTIONS }),
  col('industry', 'Industry', 'enum', 'Company', { enumOptions: INDUSTRY_OPTIONS }),
  col('sector', 'Sector', 'enum', 'Company', { enumOptions: SECTOR_OPTIONS }),
]

// ─── Assemble Catalogs ───

/** Curated catalog for earnings & splits calendar filters. */
export const CALENDAR_COLUMN_CATALOG: ColumnCatalogDef[] = [
  ...PRICE_PERFORMANCE,
  ...VOLUME,
  ...SHARE_STRUCTURE,
  ...FINANCIALS,
  ...COMPANY,
]

/** O(1) lookup by column key. */
export const CALENDAR_COLUMN_MAP: Record<string, ColumnCatalogDef> = Object.fromEntries(
  CALENDAR_COLUMN_CATALOG.map((c) => [c.key, c]),
)

/** Columns grouped by category. */
export const CALENDAR_COLUMNS_BY_CATEGORY: Record<string, ColumnCatalogDef[]> =
  CALENDAR_COLUMN_CATALOG.reduce(
    (acc, c) => {
      ;(acc[c.category] ??= []).push(c)
      return acc
    },
    {} as Record<string, ColumnCatalogDef[]>,
  )
